import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Query } from "../api/client.ts";
import type { List } from "../api/types.ts";

/** Load something once (and again when `deps` change). */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const run = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const v = await fn();
      if (id === seq.current) setData(v);
    } catch (e) {
      if (id === seq.current) setError(e);
    } finally {
      if (id === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    void run();
  }, [run]);
  return { data, setData, error, loading, reload: run };
}

/** Cursor-paginated list (`?before=<last id>`). Filters go in `params`; changing them restarts the list. */
export function useCursorList<T extends { id: string }>(path: string, params: Query, limit = 25) {
  const [items, setItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = JSON.stringify(params);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const id = ++seq.current;
    if (!path) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<List<T>>(path, { ...params, limit });
      if (id !== seq.current) return;
      setItems(r.items);
      setHasMore(r.has_more);
    } catch (e) {
      if (id === seq.current) setError(e);
    } finally {
      if (id === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    const last = items[items.length - 1];
    if (!last) return;
    const id = seq.current;
    setLoadingMore(true);
    try {
      const r = await api.get<List<T>>(path, { ...params, limit, before: last.id });
      if (id !== seq.current) return;
      setItems((xs) => [...xs, ...r.items]);
      setHasMore(r.has_more);
    } catch (e) {
      setError(e);
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, path, key, limit]);

  const replace = useCallback((item: T) => setItems((xs) => xs.map((x) => (x.id === item.id ? item : x))), []);
  return { items, hasMore, loading, loadingMore, error, reload: load, loadMore, replace, setItems };
}

export function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Media query as state (for small layout switches that CSS alone can't do). */
export function useMedia(query: string): boolean {
  const [m, setM] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return m;
}
